import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { CrmFunnelsConversationsService } from 'src/api/generated';
import ErrorReporter from 'src/monitoring/errorReporter';
import { useAlert } from 'src/contexts/alert-context';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useRealtime } from 'src/modules/shared/realtime/hooks/provider-bounded/use-realtime';
import { useRealtimeEventListener } from 'src/modules/shared/realtime/hooks/provider-bounded/use-realtime-event-listener';
import type { RealtimeEventMessage } from 'src/modules/shared/realtime/types/realtime.types';
import { listNotificationInbox } from '../services/notifications.service';
import { useNotificationsPreferences } from '../hooks/api/notifications';
import {
  NOTIFICATION_CHANNEL,
  NOTIFICATION_EVENT_KEY,
  NOTIFICATION_TYPE,
  type NotificationEventKey,
  type NotificationInAppPreferences,
  type NotificationInboxItem,
  type NotificationPreferences,
} from '../types/notification.types';

interface NotificationsRealtimeProviderProps {
  /** Authenticated application subtree that can receive notification events. */
  children: ReactNode;
}

interface SocketPayloadData {
  /** Notification inbox item emitted by the socket event. */
  item?: NotificationInboxItem;
  /** Notification event key emitted by the backend. */
  eventKey?: NotificationEventKey;
  /** In-app preferences snapshot attached to the event. */
  inApp?: NotificationInAppPreferences;
  /** Optional conversation export payload for automatic download. */
  export?: {
    /** File id generated for the export. */
    fileId?: string;
    /** Export request id used to authorize download. */
    requestId?: string;
  };
}

const EXPORT_DOWNLOAD_RETRY_ATTEMPTS = 3;
const EXPORT_DOWNLOAD_RETRY_BASE_DELAY_MS = 1000;
const EXPORT_DOWNLOAD_QUEUE_DELAY_MS = 250;
const EXPORT_INBOX_CATCH_UP_DELAY_MS = 1500;
const EXPORT_INBOX_CATCH_UP_PAGE_SIZE = 100;
const EXPORT_INBOX_CATCH_UP_MAX_PAGES = 5;
const DEFAULT_IN_APP_PREFERENCES: NotificationInAppPreferences = {
  showAlert: true,
  playSound: true,
};
const NOTIFICATIONS_INBOX_TOPIC = 'notifications.inbox';

const isPayloadData = (value: unknown): value is SocketPayloadData =>
  typeof value === 'object' && value !== null;

const getAudioContextCtor = (): typeof AudioContext | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const windowWithAudioContext = window as Window & {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };

  return (
    windowWithAudioContext.AudioContext ??
    windowWithAudioContext.webkitAudioContext ??
    null
  );
};

/**
 * Consumes shared realtime events for notification inbox and export UX updates.
 *
 * @param props - Provider props containing the authenticated app subtree.
 */
export const NotificationsRealtimeProvider: React.FC<
  NotificationsRealtimeProviderProps
> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const { subscribe } = useRealtime();
  const { data: preferences } = useNotificationsPreferences(
    isAuthenticated && !isLoading
  );
  const queryClient = useQueryClient();
  const hasUserGestureRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const preferencesRef = useRef<NotificationPreferences | null>(null);
  const inAppPreferencesRef = useRef<NotificationInAppPreferences>(
    DEFAULT_IN_APP_PREFERENCES
  );
  const processedExportDownloadsRef = useRef<Set<string>>(new Set());
  const pendingExportDownloadsRef = useRef<
    { fileId: string; requestId: string }[]
  >([]);
  const processingExportDownloadsRef = useRef(false);
  const exportCatchupTimeoutsRef = useRef<Map<string, number>>(new Map());
  const autoDownloadConversationExportRef = useRef<
    ((payloadData?: SocketPayloadData) => Promise<void>) | null
  >(null);

  useEffect(() => {
    inAppPreferencesRef.current =
      preferences?.inApp ?? DEFAULT_IN_APP_PREFERENCES;
    preferencesRef.current = preferences ?? null;
  }, [preferences]);

  useEffect(() => {
    const exportCatchupTimeouts = exportCatchupTimeoutsRef.current;

    return () => {
      exportCatchupTimeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      exportCatchupTimeouts.clear();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (hasUserGestureRef.current) {
      return;
    }

    const markGesture = () => {
      hasUserGestureRef.current = true;
      if (!audioContextRef.current) {
        const AudioContextCtor = getAudioContextCtor();
        if (AudioContextCtor) {
          const context = new AudioContextCtor();
          audioContextRef.current = context;
          context.resume().catch(() => undefined);
        }
      }
      window.removeEventListener('pointerdown', markGesture);
      window.removeEventListener('keydown', markGesture);
      window.removeEventListener('touchstart', markGesture);
    };

    window.addEventListener('pointerdown', markGesture);
    window.addEventListener('keydown', markGesture);
    window.addEventListener('touchstart', markGesture);

    return () => {
      window.removeEventListener('pointerdown', markGesture);
      window.removeEventListener('keydown', markGesture);
      window.removeEventListener('touchstart', markGesture);
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => undefined);
        audioContextRef.current = null;
      }
    };
  }, []);

  const playNotificationSound = (shouldPlaySound: boolean) => {
    if (!shouldPlaySound) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const audioContext = audioContextRef.current;
    if (!hasUserGestureRef.current || !audioContext) {
      return;
    }

    try {
      if (audioContext.state !== 'running') {
        return;
      }

      const now = audioContext.currentTime;
      const masterGain = audioContext.createGain();

      masterGain.gain.setValueAtTime(0.0001, now);
      masterGain.gain.exponentialRampToValueAtTime(0.09, now + 0.02);
      masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);
      masterGain.connect(audioContext.destination);

      const partials = [
        { frequency: 880, gain: 1.0, decay: 0.7 },
        { frequency: 1320, gain: 0.6, decay: 0.55 },
        { frequency: 1760, gain: 0.4, decay: 0.45 },
      ];

      partials.forEach(({ frequency, gain, decay }, index) => {
        const osc = audioContext.createOscillator();
        const oscGain = audioContext.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(frequency, now);
        osc.detune.setValueAtTime(index * 2, now);

        oscGain.gain.setValueAtTime(0.0001, now);
        oscGain.gain.exponentialRampToValueAtTime(gain, now + 0.01);
        oscGain.gain.exponentialRampToValueAtTime(0.0001, now + decay);

        osc.connect(oscGain);
        oscGain.connect(masterGain);
        osc.start(now);
        osc.stop(now + decay + 0.05);
      });
    } catch (error) {
      ErrorReporter.sendException(error);
    }
  };

  const resolveInAppPreferences = (
    currentPreferences: NotificationPreferences | null,
    eventKey?: NotificationEventKey,
    payloadInApp?: NotificationInAppPreferences
  ): NotificationInAppPreferences => {
    if (payloadInApp) {
      return payloadInApp;
    }

    const basePreferences =
      currentPreferences?.inApp ?? DEFAULT_IN_APP_PREFERENCES;
    if (!eventKey) {
      return basePreferences;
    }

    const rule = currentPreferences?.eventRules?.find(
      (eventRule) => eventRule.eventKey === eventKey
    );
    if (!rule?.inApp) {
      return basePreferences;
    }

    return {
      ...basePreferences,
      ...rule.inApp,
    };
  };

  const getStringOrUndefined = (value?: string): string | undefined => {
    const normalized = value?.trim();
    return normalized === '' ? undefined : normalized;
  };

  const extractConversationExportParamsFromItem = (
    item: NotificationInboxItem
  ): { fileId: string; requestId: string } | null => {
    if (typeof window === 'undefined') {
      return null;
    }

    const candidateUrls: string[] = [];
    if (item.metadata?.deepLink) {
      candidateUrls.push(item.metadata.deepLink);
    }
    (item.metadata?.links ?? []).forEach((link) => {
      if (link.url) {
        candidateUrls.push(link.url);
      }
    });

    for (const candidateUrl of candidateUrls) {
      try {
        const parsedUrl = new URL(candidateUrl, window.location.origin);
        const fileId = getStringOrUndefined(
          parsedUrl.searchParams.get('conversationExportFileId') ?? undefined
        );
        const requestId = getStringOrUndefined(
          parsedUrl.searchParams.get('conversationExportRequestId') ?? undefined
        );

        if (fileId && requestId) {
          return { fileId, requestId };
        }
      } catch {
        // Ignore malformed links and continue trying candidates.
      }
    }

    return null;
  };

  const extractConversationExportParamsFromPayload = (
    payloadData?: SocketPayloadData
  ): { fileId: string; requestId: string } | null => {
    const fileId = getStringOrUndefined(payloadData?.export?.fileId);
    const requestId = getStringOrUndefined(payloadData?.export?.requestId);
    if (fileId && requestId) {
      return { fileId, requestId };
    }

    const item = payloadData?.item;
    if (!item) {
      return null;
    }

    return extractConversationExportParamsFromItem(item);
  };

  const startBackgroundDownload = (downloadUrl: string) => {
    if (typeof document === 'undefined') {
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = downloadUrl;
    document.body.appendChild(iframe);

    window.setTimeout(() => {
      iframe.remove();
    }, 60_000);
  };

  const wait = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

  const queueExportDownload = (params: {
    fileId: string;
    requestId: string;
  }) => {
    const dedupeKey = `${params.fileId}:${params.requestId}`;
    if (processedExportDownloadsRef.current.has(dedupeKey)) {
      return;
    }

    processedExportDownloadsRef.current.add(dedupeKey);
    pendingExportDownloadsRef.current.push(params);
  };

  const downloadConversationExportWithRetry = async (params: {
    fileId: string;
    requestId: string;
  }): Promise<boolean> => {
    for (
      let attempt = 1;
      attempt <= EXPORT_DOWNLOAD_RETRY_ATTEMPTS;
      attempt += 1
    ) {
      try {
        const response =
          await CrmFunnelsConversationsService.getFunnelConversationExportDownloadUrl(
            params.fileId,
            params.requestId
          );

        if (!response.downloadUrl) {
          throw new Error('Missing download URL for conversation export');
        }

        startBackgroundDownload(response.downloadUrl);
        return true;
      } catch (error) {
        if (attempt >= EXPORT_DOWNLOAD_RETRY_ATTEMPTS) {
          ErrorReporter.sendException(error);
          return false;
        }

        const retryDelay = EXPORT_DOWNLOAD_RETRY_BASE_DELAY_MS * attempt;
        await wait(retryDelay);
      }
    }

    return false;
  };

  const processExportDownloadQueue = async () => {
    if (processingExportDownloadsRef.current) {
      return;
    }

    processingExportDownloadsRef.current = true;
    try {
      while (pendingExportDownloadsRef.current.length > 0) {
        const exportParams = pendingExportDownloadsRef.current.shift();
        if (!exportParams) {
          continue;
        }

        const dedupeKey = `${exportParams.fileId}:${exportParams.requestId}`;
        const completed =
          await downloadConversationExportWithRetry(exportParams);

        if (!completed) {
          processedExportDownloadsRef.current.delete(dedupeKey);
        }

        await wait(EXPORT_DOWNLOAD_QUEUE_DELAY_MS);
      }
    } finally {
      processingExportDownloadsRef.current = false;
    }
  };

  const catchUpExportDownloadsFromInbox = async (
    requestId: string
  ): Promise<void> => {
    try {
      let pageToken: string | undefined;
      let pageCount = 0;

      while (pageCount < EXPORT_INBOX_CATCH_UP_MAX_PAGES) {
        const inboxPage = await listNotificationInbox({
          channel: NOTIFICATION_CHANNEL.SYSTEM,
          type: NOTIFICATION_TYPE.SYSTEM,
          limit: EXPORT_INBOX_CATCH_UP_PAGE_SIZE,
          pageToken,
        });

        inboxPage.items.forEach((item) => {
          const candidate = extractConversationExportParamsFromItem(item);
          if (!candidate) {
            return;
          }

          if (candidate.requestId !== requestId) {
            return;
          }

          queueExportDownload(candidate);
        });

        pageCount += 1;
        pageToken = inboxPage.nextPageToken;
        if (!pageToken) {
          break;
        }
      }

      await processExportDownloadQueue();
    } catch (error) {
      ErrorReporter.sendException(error);
    }
  };

  const autoDownloadConversationExport = async (
    payloadData?: SocketPayloadData
  ) => {
    const exportParams =
      extractConversationExportParamsFromPayload(payloadData);
    if (!exportParams) {
      return;
    }

    queueExportDownload(exportParams);
    await processExportDownloadQueue();

    const existingTimeout = exportCatchupTimeoutsRef.current.get(
      exportParams.requestId
    );
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
    }

    const timeoutId = window.setTimeout(() => {
      exportCatchupTimeoutsRef.current.delete(exportParams.requestId);
      void catchUpExportDownloadsFromInbox(exportParams.requestId);
    }, EXPORT_INBOX_CATCH_UP_DELAY_MS);

    exportCatchupTimeoutsRef.current.set(exportParams.requestId, timeoutId);
  };

  autoDownloadConversationExportRef.current = autoDownloadConversationExport;

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      return;
    }

    return subscribe([NOTIFICATIONS_INBOX_TOPIC]);
  }, [isAuthenticated, isLoading, subscribe]);

  const handleRealtimeEvent = useCallback(
    (message: RealtimeEventMessage) => {
      if (!isAuthenticated || isLoading) {
        return;
      }

      if (message.topic !== NOTIFICATIONS_INBOX_TOPIC) {
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ['notifications-inbox'] });
      void queryClient.invalidateQueries({
        queryKey: ['notifications-unread-count'],
      });

      const payloadData = isPayloadData(message.payload.data)
        ? message.payload.data
        : undefined;
      const item = payloadData?.item;
      const isConversationExportReadyEvent =
        payloadData?.eventKey ===
        NOTIFICATION_EVENT_KEY.CONVERSATION_TABLE_EXPORT_READY;

      if (isConversationExportReadyEvent) {
        void autoDownloadConversationExportRef.current?.(payloadData);
      }

      const resolvedInApp = resolveInAppPreferences(
        preferencesRef.current,
        payloadData?.eventKey,
        payloadData?.inApp
      );
      const shouldShowAlert = resolvedInApp.showAlert;

      if (shouldShowAlert && item && !isConversationExportReadyEvent) {
        const title = item.title.trim();
        const titleSuffix = title ? `: ${title}` : '';
        const alertMessage = t('notifications.alerts.new', {
          title: titleSuffix,
        });
        showAlert(alertMessage, 'success');
      }

      if (!isConversationExportReadyEvent) {
        playNotificationSound(resolvedInApp.playSound);
      }
    },
    [isAuthenticated, isLoading, queryClient, showAlert, t]
  );

  useRealtimeEventListener(handleRealtimeEvent);

  return <>{children}</>;
};
